/**
 * closedReasons.ts — the closed-lost vocabulary, as the picker reads it.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Server half:
 * clinic-service `src/deals/closed-reason.ts`.
 *
 * ⚠️ THIS IS A MIRROR, AND THE SERVER IS THE CONTROL (R41). Everything here is
 * about what a staff member SEES and when the form lets them submit; every rule
 * that matters is enforced again in clinic-service, which refuses a close with
 * no code, refuses `other` with no note, and refuses "could not reach them"
 * under three call attempts. If the two ever disagree the server wins and the
 * user sees its message — that is why the dialog surfaces the server's own
 * words rather than re-deriving an apology from a status code.
 *
 * ⚠️ THE KEYS MUST MATCH THE SERVER'S EXACTLY. They are what is written to the
 * database and grouped in every report, so a typo here does not fail loudly —
 * it silently posts a value the server's enum rejects, and the staff member
 * sees a 400 they cannot act on. Keys are never re-spelled on either side;
 * a new reason is appended in both.
 */

/** The order they appear in the picker — commonest first, `Other` last. */
export const CLOSED_REASON_OPTIONS = [
  { code: 'not_interested', label: 'Not interested' },
  { code: 'unreachable', label: 'Could not reach them' },
  { code: 'too_expensive', label: 'Too expensive' },
  { code: 'went_elsewhere', label: 'Going elsewhere' },
  { code: 'not_eligible', label: 'Not clinically eligible' },
  { code: 'wrong_contact_details', label: 'Wrong contact details' },
  { code: 'other', label: 'Other' },
] as const;

export type ClosedReasonCode = (typeof CLOSED_REASON_OPTIONS)[number]['code'];

/**
 * The reason that asserts NO CONTACT — the only one the three-attempt rule
 * still gates, and therefore the only one the dialog warns about up front.
 */
export const NO_CONTACT_REASON: ClosedReasonCode = 'unreachable';

/** Reasons whose code says almost nothing without the staff member's own words. */
export const REASONS_NEEDING_DETAIL: readonly ClosedReasonCode[] = ['other', 'wrong_contact_details'];

export function reasonNeedsDetail(code: ClosedReasonCode | ''): boolean {
  return code !== '' && REASONS_NEEDING_DETAIL.includes(code);
}

/** Attempts a call round must log before "could not reach them" is allowed. */
export const REQUIRED_ROUND_ATTEMPTS = 3;

/**
 * Why the form cannot be submitted yet, or null when it can.
 *
 * ⚠️ IT ANSWERS BEFORE THE CLICK, and that is the point of it existing at all.
 * The drawer already knows the attempt count — it renders "1 of 3 call
 * attempts" on the card — so letting somebody choose "could not reach them",
 * type a note and press the button, only to be refused, wastes the one thing
 * the screen already had. A reason the server WILL refuse is named here while
 * they can still change it.
 *
 * `attemptsThisRound` is `null` when the board could not tell us (a card read
 * that predates the field, or a lane with no round). Null means DO NOT WARN:
 * an invented "0 of 3" on a deal that has been rung five times is worse than
 * saying nothing, and the server still refuses correctly either way.
 */
export function closeBlockedReason(
  code: ClosedReasonCode | '',
  detail: string,
  attemptsThisRound: number | null,
): string | null {
  if (code === '') return 'Choose a reason.';
  if (reasonNeedsDetail(code) && !detail.trim()) return 'Add a short note saying what happened.';
  if (code === NO_CONTACT_REASON && attemptsThisRound !== null && attemptsThisRound < REQUIRED_ROUND_ATTEMPTS) {
    const left = REQUIRED_ROUND_ATTEMPTS - attemptsThisRound;
    return (
      `This round has ${attemptsThisRound} of ${REQUIRED_ROUND_ATTEMPTS} call attempts. ` +
      `Log ${left} more, or — if you did reach them — choose the reason they gave instead.`
    );
  }
  return null;
}
